import classnames from 'classnames';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { FilterPanel, useClientSideNav } from '@ecosystem/newron-client-lib';
import { usePageConfig } from '@utils/pageConfig/PagesConfigContext';
import ListFilters from '../list-filters/ListFilters';

import { useWindowDimensions } from '@utils/hook/window-dimensions/WindowDimensions';
import { FilterTitle, PageName, PageType } from '@utils/pageConfig/Types';
import { ProductType } from '@pages/product-analyzer/Product';
import {
  useFetchProductGroupFeatureValue,
  useFetchProductGroupPriceBandValueList,
} from '@utils/hook/sideFilter/UseFetchProductGroupFeatureValue';
import { getEffectiveProductType } from '@models/SubscribedProductType';
import { useSelector } from 'react-redux';
import { IRootState } from '@state/reducers/Reducers';
import ListSavedFilters from '../list-saved-filters/ListSavedFilters';
import ListFiltersProduct from '@components/list-filters-product/ListFiltersProduct';
import { useGetPersistedFilters } from '@utils/hook/saveFilter/UseGetPersistedFilters';

export enum Tabs {
  Configure = 'Configure',
  SavedFilters = 'Saved filters',
}

/* eslint-disable-next-line */
export interface FiltersTrayProps {}

// #REFACTOR, this is probably a dirty fix, proper way to do it is wait for main page to finish loading subscription before assigning this value properly
export const getEffectiveProductTypeCatch = (subsProductType: ProductType[], pageName: PageName): ProductType => {
  try {
    return getEffectiveProductType(subsProductType, pageName);
  } catch (e) {
    console.error('Failed to get effective product type:', e);
    return ProductType.PerformanceAnalyzer;
  }
};

const FiltersTray: React.FC<FiltersTrayProps> = () => {
  const retailSidebarRef = useRef(null);
  const { refetch } = useGetPersistedFilters();
  const pagesConfig = usePageConfig();
  const subscribedProductTypes = useSelector(
    (state: IRootState) => state.subscribedProductTypes.subscribedProductTypes,
  );

  const { viewportWidth } = useWindowDimensions();
  const { setExpanded } = useClientSideNav();

  const EXPANDED_WIDTH_SMALL_SCREEN = '254px';
  const EXPANDED_WIDTH_BIG_SCREEN = '320px';
  const DESKTOP_WIDTH_EXTRA_LARGE = 1920;
  const NO_WIDTH = '0';

  const [activeTab, setActiveTab] = useState<Tabs>(Tabs.Configure);

  /**
   * construct input to get list of product group of feature value
   */
  const getProductGroupFeatureValueParam = (productType: ProductType) => {
    return {
      clientId: pagesConfig.clientId,
      productType,
      country: getSelectedLabels(FilterTitle.COUNTRY),
      periodType: pagesConfig.selectedTimeFrame,
      productGroup: getPgsByCountry(),
      productDataType:
        pagesConfig.pageName === PageName.RI_PRODUCT ? ProductType.ProductAnalyzer : ProductType.PerformanceAnalyzer,
    };
  };

  /**
   * return list of country or pg in String array based on filter title
   */
  const getSelectedLabels = (filterTitle: string) => {
    return getAppliedFilters()
      .filter((f) => f.filterTitle === filterTitle)
      .flatMap((f) => f.options)
      .map((opt) => opt.value);
  };

  /**
   * return Selected filters array which contains Product Group and Country Option list
   */
  const getAppliedFilters = () => {
    return pagesConfig.appliedFilters
      .filter((sf) => sf.pageType === pagesConfig.pageType)
      .flatMap((sf) => sf.selectedFilters);
  };

  /**
   * get all product groups by country
   */
  const getPgsByCountry = () => {
    return pagesConfig.pgsByAppliedCountry
      .filter((countryFilterPair) => countryFilterPair.filter.filterTitle === FilterTitle.PRODUCT_GROUP)
      .filter((countryFilterPair) => countryFilterPair.periodType === pagesConfig.selectedTimeFrame?.toUpperCase())
      .flatMap((countryFilterPair) => countryFilterPair.filter.options)
      .map((options) => options.value);
  };

  const isLoadingSaveFilter = () => {
    return loading ? emptyFilters() : <ListSavedFilters />;
  };

  const pageProductType: ProductType = getEffectiveProductTypeCatch(subscribedProductTypes, pagesConfig.pageName);

  const distinctProductType = subscribedProductTypes.filter(
    (productType, index) => subscribedProductTypes.indexOf(productType) === index,
  );

  useFetchProductGroupFeatureValue(getProductGroupFeatureValueParam(pageProductType));
  const { loading } = useFetchProductGroupPriceBandValueList({
    clientId: pagesConfig.clientId,
    country: pagesConfig.savedFilterCountryList,
    productType: distinctProductType,
  });

  const renderFiltersTray = () => {
    if (pagesConfig.pageType === PageType.RI_PRODUCT) {
      return activeTab === Tabs.Configure ? <ListFiltersProduct /> : isLoadingSaveFilter();
    }
    return activeTab === Tabs.Configure ? <ListFilters /> : isLoadingSaveFilter();
  };

  useLayoutEffect(() => {
    const parentElement = retailSidebarRef.current?.parentElement;
    if (parentElement) {
      parentElement.classList.add('retail-sidebar-saved-filters');
    }
  });

  useEffect(() => {
    renderFiltersTray();
  }, [loading]);

  useEffect(() => {
    if (pagesConfig.filtersTrayOpened) {
      if (viewportWidth > DESKTOP_WIDTH_EXTRA_LARGE) {
        document.documentElement.style.setProperty('--right-sidebar-width', EXPANDED_WIDTH_BIG_SCREEN);
      } else {
        setExpanded(false);

        document.documentElement.style.setProperty('--right-sidebar-width', EXPANDED_WIDTH_SMALL_SCREEN);
      }
    } else {
      document.documentElement.style.setProperty('--right-sidebar-width', NO_WIDTH);
    }

    return () => {
      document.documentElement.style.setProperty('--right-sidebar-width', NO_WIDTH);
    };
  }, [pagesConfig.filtersTrayOpened, viewportWidth]);

  useEffect(() => {
    refetch();
  }, [pagesConfig.pageType]);

  const emptyFilters = () => {
    return (
      <div className="p-4">
        <h3 data-testid="load-saved-filters" className="pb-4 text-sm text-default font-bold">
          Saved filters on loading
        </h3>
        <p className="text-default text-sm">A few moments for the saved filters to be available</p>
      </div>
    );
  };

  return (
    <FilterPanel>
      <div ref={retailSidebarRef} className="retail-sidebar-tabs border-b border-default px-4">
        <button
          data-test-id={'filters-configure-tab'}
          className={classnames(['mr-6', 'py-4 text-center'], {
            ['text-brand border-b-2 border-brand']: activeTab === Tabs.Configure,
          })}
          onClick={() => {
            setActiveTab(Tabs.Configure);
          }}
        >
          {Tabs.Configure}
        </button>
        <button
          data-test-id={'filters-saved-tab'}
          className={classnames(['mr-6', 'py-4 text-center'], {
            ['text-brand border-b-2 border-brand']: activeTab === Tabs.SavedFilters,
          })}
          onClick={() => {
            setActiveTab(Tabs.SavedFilters);
          }}
        >
          {Tabs.SavedFilters}
        </button>
      </div>
      {renderFiltersTray()}
    </FilterPanel>
  );
};

export default FiltersTray;
